import React, { useState, useEffect, useRef, useContext } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";
import emojiRegex from "emoji-regex";
import { v4 as uuidv4 } from "uuid";

import { makeStyles } from "@material-ui/core/styles";
import ListItem from "@material-ui/core/ListItem";
import Avatar from "@material-ui/core/Avatar";
import IconButton from "@material-ui/core/IconButton";
import { i18n } from "../../translate/i18n";
import { getTokens, brandOn } from "../../styles/tokens";
import CheckIcon from "@material-ui/icons/CheckCircle";
import ReplayIcon from "@material-ui/icons/Replay";
import ClearOutlinedIcon from "@material-ui/icons/ClearOutlined";
import api from "../../services/api";
import { Dialog, DialogContent } from "@material-ui/core";

import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import MutedIcon from "../MuteDialog/MutedIcon";
import { useMutes } from "../../context/Mutes/MutesContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

import facebookIcon from "../../assets/facebook.png";
import insatagramIcon from "../../assets/instagram.png";
import whatsappIcon from "../../assets/whatsapp.png";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

/* Linha plana no padrão da lista de conversas (TicketListItemCustom): avatar com o canal,
 * nome + hora, trecho da última mensagem + selo verde de não lidas, selos (fila, conexão,
 * grupo, atendente, tags) e as ações à direita. Cores de fila/tag/atendente vêm dos dados. */
const useStyles = makeStyles((theme) => {
    const t = getTokens(theme.palette.type, theme.palette.primary.main);
    const isDark = theme.palette.type === "dark";
    const wa = isDark
        ? { hover: "#202c33", selected: "#2a3942", unread: "#00a884", unreadText: "#111b21" }
        : { hover: "#f5f6f6", selected: "#f0f2f5", unread: "#25d366", unreadText: "#ffffff" };
    const ellipsis = {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    };
    return {
        ticket: {
            position: "relative",
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            width: "100%",
            minHeight: 74,
            margin: 0,
            padding: "10px 12px 9px 14px",
            boxSizing: "border-box",
            borderBottom: `1px solid ${t.line}`,
            "&:hover": { backgroundColor: wa.hover },
            "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: wa.selected },
        },

        pendingTicket: {
            cursor: "unset",
        },

        avatarWrap: {
            position: "relative",
            flex: "none",
            width: 46,
            height: 46,
            marginTop: 1,
        },
        avatar: {
            width: 46,
            height: 46,
            cursor: "pointer",
            "&:hover": {
                opacity: 0.85,
            },
        },
        channelDot: {
            position: "absolute",
            right: -3,
            bottom: -3,
            width: 18,
            height: 18,
            borderRadius: "50%",
            border: `2px solid ${t.paper}`,
            backgroundColor: t.paper,
            boxSizing: "border-box",
            pointerEvents: "none",
        },

        body: {
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
        },
        row: {
            display: "flex",
            alignItems: "center",
            gap: 6,
            minWidth: 0,
        },
        name: {
            flex: 1,
            minWidth: 0,
            fontSize: 15,
            fontWeight: 600,
            lineHeight: "20px",
            color: t.text,
            ...ellipsis,
        },
        time: {
            flex: "none",
            fontSize: 11,
            lineHeight: "16px",
            color: t.textMuted,
            fontVariantNumeric: "tabular-nums",
        },
        timeUnread: {
            color: wa.unread,
            fontWeight: 700,
        },
        lastMessage: {
            flex: 1,
            minWidth: 0,
            fontSize: 12.5,
            lineHeight: "16px",
            color: t.textSecondary,
            ...ellipsis,
            "& p": {
                margin: 0,
                display: "inline",
            },
        },
        lastMessageUnread: {
            color: t.text,
            fontWeight: 500,
        },
        rating: {
            flex: "none",
            fontSize: 14,
            lineHeight: "16px",
        },
        mutedIcon: {
            fontSize: 14,
            color: t.textMuted,
        },
        unread: {
            flex: "none",
            minWidth: 20,
            height: 20,
            padding: "0 6px",
            borderRadius: 10,
            backgroundColor: wa.unread,
            color: wa.unreadText,
            fontSize: 11.5,
            fontWeight: 700,
            lineHeight: "20px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontVariantNumeric: "tabular-nums",
        },
        unreadMuted: {
            backgroundColor: isDark ? "#8696a0" : "#9aa5ab",
            color: isDark ? "#111b21" : "#ffffff",
        },

        meta: {
            flex: 1,
            minWidth: 0,
            minHeight: 18,
            display: "flex",
            alignItems: "center",
            gap: 4,
            overflow: "hidden",
        },
        badge: {
            flex: "0 1 auto",
            minWidth: 0,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            height: 18,
            maxWidth: 96,
            padding: "0 6px",
            borderRadius: 9,
            fontSize: 11,
            fontWeight: 500,
            lineHeight: "18px",
            backgroundColor: t.page,
            color: t.textSecondary,
            border: `1px solid ${t.line}`,
            boxSizing: "border-box",
            whiteSpace: "nowrap",
            overflow: "hidden",
        },
        badgeText: {
            ...ellipsis,
        },
        // selo com a cor do dado (atendente, tag): sem borda, texto pelo contraste
        badgeColored: {
            border: 0,
            fontWeight: 600,
        },
        dot: {
            flex: "none",
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: t.textMuted,
        },

        actions: {
            flex: "none",
            alignSelf: "center",
            display: "flex",
            alignItems: "center",
            gap: 2,
        },
        actionBtn: {
            width: 32,
            height: 32,
            padding: 0,
            color: t.textSecondary,
            "& svg": {
                fontSize: 19,
            },
            "&:hover": {
                color: t.accent,
            },
        },
        actionDanger: {
            "&:hover": {
                color: t.semantic.error,
            },
        },

        // Estilos para o modal da imagem
        imageModal: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        imageModalContent: {
            outline: "none",
            maxWidth: "90vw",
            maxHeight: "90vh",
        },
        expandedImage: {
            width: "100%",
            height: "auto",
            maxWidth: "500px",
            borderRadius: t.radius.md,
        },
    };
});

const getAvatarChannel = (channel) => {
    if (channel === "facebook") {
        return facebookIcon;
    }

    if (channel === "whatsapp") {
        return whatsappIcon;
    }

    if (channel === "whatsappapi") {
        return whatsappIcon;
    }

    if (channel === "instagram") {
        return insatagramIcon;
    }
};

const TicketListItem = ({ ticket }) => {
    const classes = useStyles();
    const { isMuted } = useMutes();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const { ticketId } = useParams();
    const isMounted = useRef(true);
    const { user } = useContext(AuthContext);
    const { setCurrentTicket, setTabOpen } = useContext(TicketsContext);
    const [imageModalOpen, setImageModalOpen] = useState(false); // Estado para o modal da imagem

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Função para abrir modal da imagem
    const handleImageClick = (e) => {
        e.stopPropagation(); // Prevenir que o clique no avatar selecione o ticket
        if (ticket?.contact?.urlPicture) {
            setImageModalOpen(true);
        }
    };

    // Função para fechar modal da imagem
    const handleImageModalClose = () => {
        setImageModalOpen(false);
    };

    function getRatingIcon(rate) {
        let icon = "";
        if (rate === 1) {
            icon = "😡";
        } else if (rate === 2) {
            icon = "😠";
        } else if (rate === 3) {
            icon = "😐";
        } else if (rate === 4) {
            icon = "😃";
        } else if (rate === 5) {
            icon = "😍";
        }

        return icon;
    }


    const handleAcepptTicket = async (ticket) => {
        setLoading(true);
        try {
            await api.put(`/tickets/${ticket.id}`, {
                status: "open",
                userId: user?.id,
            });
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
        if (isMounted.current) {
            setLoading(false);
        }
        history.push(`/tickets/${ticket.uuid}`);
    };

    const handleAcepptTicketBot = async (ticket) => {
        // alert("Bot");
        setLoading(true);
        try {
            await api.put(`/tickets/${ticket.id}`, {
                status: "open",
                userId: user?.id,
            });
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
        if (isMounted.current) {
            setLoading(false);
        }
        history.push(`/tickets/${ticket.uuid}`);
    };

    const handleReopenTicket = async (ticket) => {
        setLoading(true);
        try {
            await api.put(`/tickets/${ticket.id}`, {
                status: "open",
                userId: user?.id,
            });
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
        if (isMounted.current) {
            setLoading(false);
        }
        history.push(`/tickets/${ticket.uuid}`);
    };

    const handleViewTicket = async (ticket) => {
        setLoading(true);
        try {
            await api.put(`/tickets/${ticket.id}`, {
                status: "pending",
            });
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
        if (isMounted.current) {
            setLoading(false);
        }
        history.push(`/tickets/${ticket.uuid}`);
    };

    const handleSelectTicket = (ticket) => {
        const code = uuidv4();
        const { id, uuid } = ticket;
        setTabOpen(ticket.status)
        setCurrentTicket({ id, uuid, code });
    };

    const handleClosedTicket = async (ticket) => {
        setLoading(true);
        try {
            await api.put(`/tickets/${ticket.id}`, {
                status: "closed",
            });
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
        if (isMounted.current) {
            setLoading(false);
        }
    };

    const renderUserName = (name) => {
        let str = name.replace(emojiRegex(), "").trim();

        const firstName = str.split(" ")[0];

        return firstName;
    };

    const queueColor = ticket.queue?.color || "#7C7C7C";
    const userColor = ticket.user?.color === "" || !ticket.user?.color ? "#7C7C7C" : ticket.user.color;
    const hasUnread = Number(ticket.unreadMessages) > 0;
    const muted = isMuted(ticket.contactId || ticket.contact?.id);
    const rating =
        ticket.status === "closed" && ticket?.userRating
            ? getRatingIcon(ticket?.userRating?.rate)
            : null;

    return (
        <React.Fragment key={ticket.id}>
            <ListItem
                dense
                button
                onClick={(e) => {
                    handleSelectTicket(ticket)
                }}
                selected={ticketId && +ticketId === ticket.id}
                className={clsx(classes.ticket, {
                    [classes.pendingTicket]: ticket.status === "pending",
                })}
            >
                <span className={classes.avatarWrap}>
                    <Avatar
                        alt={ticket?.contact?.name}
                        src={ticket?.contact?.urlPicture}
                        className={classes.avatar}
                        onClick={handleImageClick}
                    />
                    {getAvatarChannel(ticket?.channel) && (
                        <img
                            className={classes.channelDot}
                            alt={ticket?.channel}
                            src={getAvatarChannel(ticket?.channel)}
                        />
                    )}
                </span>

                <div className={classes.body}>
                    <div className={classes.row}>
                        <span className={classes.name}>{ticket.contact.name}</span>
                        <MutedIcon contactId={ticket.contactId || ticket.contact?.id} className={classes.mutedIcon} />
                        <span className={clsx(classes.time, hasUnread && classes.timeUnread)}>
                            {isSameDay(parseISO(ticket.updatedAt), new Date())
                                ? format(parseISO(ticket.updatedAt), "HH:mm")
                                : format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}
                        </span>
                    </div>

                    <div className={classes.row}>
                        {rating && <span className={classes.rating}>{rating}</span>}
                        <span className={clsx(classes.lastMessage, hasUnread && classes.lastMessageUnread)}>
                            {ticket.lastMessage ? (
                                <MarkdownWrapper>
                                    {ticket.lastMessage}
                                </MarkdownWrapper>
                            ) : (
                                "\u00a0"
                            )}
                        </span>
                        {hasUnread && (
                            <span className={clsx(classes.unread, muted && classes.unreadMuted)}>{ticket.unreadMessages}</span>
                        )}
                    </div>

                    <div className={classes.meta}>
                        <Tooltip arrow title={ticket.queue?.name || "Sem departamento"}>
                            <span className={classes.badge}>
                                <span className={classes.dot} style={{ backgroundColor: queueColor }} />
                                <span className={classes.badgeText}>
                                    {ticket.queue?.name || "Sem departamento"}
                                </span>
                            </span>
                        </Tooltip>
                        {ticket.whatsappId && ticket.whatsapp?.name && (
                            <span
                                className={classes.badge}
                                title={i18n.t("ticketsList.connectionTitle")}
                            >
                                <span className={classes.badgeText}>{ticket.whatsapp?.name}</span>
                            </span>
                        )}
                        {ticket.isGroup && (
                            <span className={classes.badge}>Grupo</span>
                        )}
                        {ticket.user?.id &&
                            user.profile.toUpperCase() === "ADMIN" && (
                                <span
                                    key={ticket.user.id}
                                    className={clsx(classes.badge, classes.badgeColored)}
                                    title={renderUserName(ticket.user.name)}
                                    style={{ backgroundColor: userColor, color: brandOn(userColor) }}
                                >
                                    <span className={classes.badgeText}>{renderUserName(ticket.user.name)}</span>
                                </span>
                            )}
                        {ticket.tags?.length > 0 && (
                            <>
                                <span
                                    key={ticket.tags[0].id}
                                    className={clsx(classes.badge, classes.badgeColored)}
                                    title={ticket.tags[0].name}
                                    style={{
                                        backgroundColor: ticket.tags[0].color,
                                        color: brandOn(ticket.tags[0].color),
                                    }}
                                >
                                    <span className={classes.badgeText}>{ticket.tags[0].name}</span>
                                </span>
                                {ticket.tags.length > 1 && (
                                    <span
                                        key={ticket.tags[1].id}
                                        className={clsx(classes.badge, classes.badgeColored)}
                                        title={ticket.tags[1].name}
                                        style={{
                                            backgroundColor: ticket.tags[1].color,
                                            color: brandOn(ticket.tags[1].color),
                                        }}
                                    >
                                        +{ticket.tags.length - 1}
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className={classes.actions}>
                    {ticket.status === "pending" && (
                        <Tooltip arrow title="Aceitar">
                            <IconButton
                                className={classes.actionBtn}
                                aria-label="Aceitar"
                                loading={loading.toString()}
                                onClick={(e) =>
                                    ticket.isBot
                                        ? handleAcepptTicketBot(ticket)
                                        : handleAcepptTicket(ticket)
                                }
                            >
                                <CheckIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    {(ticket.status === "pending" && (user.showDashboard === "enabled" || user.profile === "admin")) && (
                        <Tooltip arrow title="Encerrar">
                            <IconButton
                                className={clsx(classes.actionBtn, classes.actionDanger)}
                                aria-label="Encerrar"
                                onClick={(e) => handleClosedTicket(ticket)}
                            >
                                <ClearOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    {ticket.status === "open" && (
                        <Tooltip arrow title="Devolver para aguardando">
                            <IconButton
                                className={classes.actionBtn}
                                aria-label="Devolver para aguardando"
                                onClick={(e) => handleViewTicket(ticket)}
                            >
                                <ReplayIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    {ticket.status === "open" && (
                        <Tooltip arrow title="Encerrar">
                            <IconButton
                                className={clsx(classes.actionBtn, classes.actionDanger)}
                                aria-label="Encerrar"
                                onClick={(e) => handleClosedTicket(ticket)}
                            >
                                <ClearOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    {ticket.status === "closed" && (
                        <Tooltip arrow title="Reabrir">
                            <IconButton
                                className={classes.actionBtn}
                                aria-label="Reabrir"
                                onClick={(e) => handleReopenTicket(ticket)}
                            >
                                <ReplayIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            </ListItem>

            {/* Modal da Imagem */}
            <Dialog
                open={imageModalOpen}
                onClose={handleImageModalClose}
                className={classes.imageModal}
                maxWidth="md"
                fullWidth
            >
                <DialogContent className={classes.imageModalContent}>
                    <img 
                        src={ticket?.contact?.urlPicture} 
                        alt={ticket?.contact?.name || "Foto do contato"}
                        className={classes.expandedImage}
                    />
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
};

export default TicketListItem;